
import { useState } from 'react';
import { AlertTriangle, FilesIcon, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { 
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useAuth } from '@/contexts/AuthContext';
import { useItineraryRequests } from '@/hooks/useItineraryRequests';
import { ItineraryRequestFilters } from '@/components/itinerary-requests/ItineraryRequestFilters';
import { ItineraryRequestsTable } from '@/components/itinerary-requests/ItineraryRequestsTable';
import { ItineraryRequestDetail } from '@/components/itinerary-requests/ItineraryRequestDetail';
import { exportBatchRequestsToPDF } from '@/utils/pdfExport';

const ItineraryRequests = () => {
  const { user, isAdmin, profile } = useAuth();
  const {
    requests,
    isLoading,
    error,
    isError,
    selectedStatus,
    setSelectedStatus,
    selectedRequest,
    setSelectedRequest,
    isDetailModalOpen,
    setIsDetailModalOpen,
    selectedRequests,
    manualRefreshing,
    handleViewDetails,
    handleUpdateStatus,
    toggleReadStatus,
    handleRetry,
    toggleRequestSelection,
    isSelected
  } = useItineraryRequests();

  const handleBatchExport = () => {
    if (!requests) return;
    
    try {
      if (selectedRequests.length > 0) {
        const requestsToExport = requests.filter(r => selectedRequests.includes(r.id));
        exportBatchRequestsToPDF(requestsToExport);
        toast.success(`Exported ${requestsToExport.length} requests to PDF`);
      } else {
        exportBatchRequestsToPDF(requests);
        toast.success(`Exported ${requests.length} requests to PDF`);
      }
    } catch (error) {
      console.error('Batch PDF export error:', error);
      toast.error('Failed to export batch PDF');
    }
  };

  if (!user) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Authentication Required</CardTitle>
        </CardHeader>
        <CardContent>
          <p>Please log in to view itinerary requests.</p>
        </CardContent>
      </Card>
    );
  }

  if (!isAdmin()) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Access Denied</CardTitle>
        </CardHeader>
        <CardContent>
          <p>You don't have permission to view this page. Admin access is required.</p>
        </CardContent>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Error</CardTitle>
        </CardHeader>
        <CardContent>
          <Alert variant="destructive" className="mb-4">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Failed to load itinerary requests</AlertTitle>
            <AlertDescription>
              {error instanceof Error ? error.message : 'Unknown error'}
            </AlertDescription>
          </Alert>
          <p className="text-sm text-muted-foreground mt-4">
            This might be due to a database permission issue or RLS policy configuration.
            If you're seeing this error after the policy fix, it may be related to your user role assignment.
          </p>
          <p className="text-sm font-medium mt-2">Possible solutions:</p>
          <ul className="list-disc pl-5 mt-1 text-sm">
            <li>Make sure your user account has the admin role correctly set</li>
            <li>Try logging out and logging back in</li>
            <li>Check that your browser's session is not corrupted</li>
          </ul>
          <Button 
            variant="default" 
            className="mt-4"
            onClick={handleRetry}
            disabled={manualRefreshing}
          >
            {manualRefreshing ? (
              <>
                <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                Retrying...
              </>
            ) : (
              <>
                <RefreshCw className="mr-2 h-4 w-4" />
                Retry
              </>
            )}
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Itinerary Requests</h2>
          <p className="text-muted-foreground">
            Manage customer itinerary requests from the contact form
          </p>
        </div>
        <ItineraryRequestFilters
          selectedStatus={selectedStatus}
          onStatusChange={setSelectedStatus}
          onRefresh={handleRetry}
          isRefreshing={manualRefreshing}
        />
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Recent Requests</CardTitle>
            <CardDescription>
              View and manage customer itinerary requests from the contact form
            </CardDescription>
          </div>
          <Button
            variant="outline"
            onClick={handleBatchExport}
            disabled={isLoading || !requests || requests.length === 0}
            title="Export to PDF"
          >
            <FilesIcon className="mr-2 h-4 w-4" />
            {selectedRequests.length > 0 
              ? `Export (${selectedRequests.length})` 
              : 'Export All'}
          </Button>
        </CardHeader>
        <CardContent>
          <ItineraryRequestsTable
            requests={requests}
            isLoading={isLoading}
            onViewDetails={handleViewDetails}
            onUpdateStatus={handleUpdateStatus}
            onToggleReadStatus={toggleReadStatus}
            onToggleSelection={toggleRequestSelection}
            isSelected={isSelected}
          />
        </CardContent>
      </Card>

      <ItineraryRequestDetail
        request={selectedRequest}
        isOpen={isDetailModalOpen}
        onOpenChange={setIsDetailModalOpen}
      />
    </div>
  );
};

export default ItineraryRequests;
